import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../../Components/Sidebar";
import { FiUpload } from "react-icons/fi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageComponent from "../../Components/PageComponent";
import CqaStaffTable from "../../Components/CqaStaffTable";
import { toast } from "react-hot-toast";
import {
  fetchCqaStaffData,
  addCqaStaffData,
} from "../../redux/actions/CQAAction";

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: [] }],
    ["bold", "italic", "underline"],
    [{ align: [] }],
    ["clean"],
  ],
};

const formats = ["font", "header", "bold", "italic", "underline", "align"];

function CQAStaff() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: staffList = [], loading } = useSelector(
    (state) => state.CQA.cqaStaff || { data: [] }
  );

  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    qualifications: "",
    description: "",
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchCqaStaffData());
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
      setFormData((prev) => ({ ...prev, image: file }));
    }
  };

  const stripHtml = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const resetForm = () => {
    setFormData({
      name: "",
      designation: "",
      qualifications: "",
      description: "",
      image: null,
    });
    setImagePreview(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    if (!formData.designation.trim()) {
      toast.error("Designation is required.");
      return;
    }
    if (!stripHtml(formData.qualifications).trim()) {
      toast.error("Qualifications cannot be empty.");
      return;
    }
    if (!formData.image) {
      toast.error("A profile photo is required.");
      return;
    }

    const formPayload = new FormData();
    formPayload.append("name", formData.name);
    formPayload.append("designation", formData.designation);
    formPayload.append("qualifications", formData.qualifications);
    formPayload.append("description", formData.description);
    formPayload.append("file", formData.image);

    setIsSaving(true);
    try {
      await dispatch(addCqaStaffData(formPayload));
      toast.success("CQA staff member added successfully!");
      resetForm();
      dispatch(fetchCqaStaffData());
    } catch (error) {
      toast.error(
        `Error adding CQA staff: ${
          error.response?.data?.message || "Something went wrong."
        }`
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <PageComponent>
        <div className="flex w-screen h-screen overflow-x-hidden bg-[#F8F7FA]">
          <div className="fixed top-0 left-0 w-64 h-full overflow-y-auto bg-white shadow-md">
            <Sidebar />
          </div>

          <div className="flex-1 p-6 ml-64 overflow-y-auto">
            <div className="mb-6">
              <div className="flex items-center space-x-2 text-3xl text-gray-500">
                <span>Gallery & News</span>
                <span>/</span>
                <span className="text-black">Center for Quality Assurance (CQA)</span>
              </div>
              <div className="flex items-center flex-wrap mt-4 gap-4">
                <button
                  className="px-4 py-2 text-[#92288C] border border-[#92288C] rounded-md shadow hover:bg-gray-100"
                  onClick={() => navigate("/gallery&News/cqa-description")}
                >
                  Add Description
                </button>
                <button
                  className="px-4 py-2 text-[#92288C] border border-[#92288C] rounded-md shadow hover:bg-gray-100"
                  onClick={() => navigate("/gallery&News/add-cqa")}
                >
                  Add New Resource
                </button>
                <button
                  className="px-4 py-2 text-[#92288C] border border-[#92288C] rounded-md shadow hover:bg-gray-100"
                  onClick={() => navigate("/gallery&News/get-cqa")}
                >
                  Resource List
                </button>
                <button className="px-4 py-2 text-white bg-[#92288C] rounded-md shadow hover:bg-[#6C0B67]">
                  CQA Staff
                </button>
              </div>
            </div>

            <div className="p-6 bg-white rounded-lg shadow-md">
              <form onSubmit={handleSave}>
                <h2 className="mb-4 text-xl font-semibold text-gray-700">Add CQA Staff</h2>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="name">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="designation">
                      Designation <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="designation"
                      name="designation"
                      type="text"
                      value={formData.designation}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="relative flex flex-col items-center justify-center w-full h-[225px] p-5 border border-gray-300 border-dashed rounded-md">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="absolute inset-0 object-cover w-full h-full opacity-50"
                      />
                    ) : (
                      <>
                        <FiUpload className="w-10 h-10 p-2 mb-3 text-gray-400 bg-gray-100 border-none rounded-md" />
                        <p className="text-black">Drag and drop image here</p>
                        <p className="text-sm text-center text-gray-400">
                          Image size should 300 px x 250 px (width x height)
                        </p>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="cqa-staff-file-upload"
                    />
                    <label
                      htmlFor="cqa-staff-file-upload"
                      className="px-4 py-2 mt-2 text-[#92288C] font-semibold bg-[#92288C] bg-opacity-10 rounded-md cursor-pointer z-10"
                    >
                      Browse Image
                    </label>
                  </div>
                  <div className="col-span-2">
                    <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="qualifications">
                      Qualifications <span className="text-red-500">*</span>
                    </label>
                    <ReactQuill
                      id="qualifications"
                      value={formData.qualifications}
                      onChange={(value) => setFormData((prev) => ({ ...prev, qualifications: value }))}
                      theme="snow"
                      modules={modules}
                      formats={formats}
                      className="h-[160px]"
                    />
                  </div>
                </div>

                <div className="mt-16">
                  <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="description">
                    Description <span className="text-gray-400">(optional)</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                  />
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className={`px-6 py-2 text-white rounded-md ${
                      isSaving ? "bg-gray-400 cursor-not-allowed" : "bg-[#92288C] hover:bg-[#6C0B67]"
                    }`}
                  >
                    {isSaving ? "Adding..." : "Add"}
                  </button>
                </div>
              </form>
            </div>

            <CqaStaffTable MemberData={staffList} loading={loading} />
          </div>
        </div>
      </PageComponent>
    </>
  );
}

export default CQAStaff;
